// @intent: Minimal Pixi-adapter tween support for R6 PlayableAPI numeric properties
// @flow: PixiPlayableAPI.tween -> createNumericTween mutates adapter-owned target refs -> finished promise resolves
// @stale: update if PlayableAPI.tween adds properties or timeline semantics

import type {
  PlayableTweenEasing,
  PlayableTweenHandle,
  PlayableTweenOptions,
  PlayableTweenProps,
} from '@mintyads/playable-api';
import type { RuntimeProbeTweenSnapshot } from '../probe/runtimeProbeTypes';

const SUPPORTED_PROPS = new Set(['x', 'y', 'alpha', 'width', 'height', 'rotation', 'scale']);
const SUPPORTED_EASINGS = new Set<PlayableTweenEasing>(['linear', 'easeInQuad', 'easeOutQuad', 'easeInOutQuad', 'backOut']);

type RuntimeTweenHandle = PlayableTweenHandle & Pick<Promise<void>, 'then' | 'catch' | 'finally'>;

export function validateTweenProps(props: Record<string, number>): string[] {
  return Object.keys(props).filter((key) => !SUPPORTED_PROPS.has(key));
}

export function normalizeTweenDurationMs(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 1;
  return Math.max(1, value);
}

export function resolveTweenDurationMs(options: PlayableTweenOptions | { readonly durationMs?: unknown; readonly duration?: unknown }): number {
  const candidate = options as { readonly durationMs?: unknown; readonly duration?: unknown };
  if (typeof candidate.durationMs === 'number') return normalizeTweenDurationMs(candidate.durationMs);
  if (typeof candidate.duration === 'number') return normalizeTweenDurationMs(candidate.duration);
  return normalizeTweenDurationMs(Number.NaN);
}

export function resolveTweenDelayMs(options: PlayableTweenOptions | { readonly delayMs?: unknown }): number {
  const delayMs = options.delayMs;
  return typeof delayMs === 'number' && Number.isFinite(delayMs)
    ? Math.max(0, delayMs)
    : 0;
}

export function validateTweenEasing(easing: unknown): easing is PlayableTweenEasing | undefined {
  return easing === undefined || (typeof easing === 'string' && SUPPORTED_EASINGS.has(easing as PlayableTweenEasing));
}

export function easeTweenProgress(progress: number, easing: PlayableTweenEasing | undefined): number {
  switch (easing ?? 'linear') {
    case 'linear':
      return progress;
    case 'easeInQuad':
      return progress * progress;
    case 'easeOutQuad':
      return 1 - ((1 - progress) * (1 - progress));
    case 'easeInOutQuad':
      return progress < 0.5
        ? 2 * progress * progress
        : 1 - ((-2 * progress + 2) * (-2 * progress + 2)) / 2;
    case 'backOut': {
      const c1 = 1.70158;
      const c3 = c1 + 1;
      return 1 + c3 * ((progress - 1) ** 3) + c1 * ((progress - 1) ** 2);
    }
    default:
      return progress;
  }
}

function createObservedRejectedFinished(message: string): Promise<void> {
  const promise = Promise.reject(new Error(message));
  void promise.catch(() => undefined);
  return promise;
}

function createTweenHandle(input: {
  readonly id: string;
  readonly finished: Promise<void>;
  readonly stop: () => void;
}): PlayableTweenHandle {
  const handle: RuntimeTweenHandle = {
    id: input.id,
    finished: input.finished,
    stop: input.stop,
    then: input.finished.then.bind(input.finished),
    catch: input.finished.catch.bind(input.finished),
    finally: input.finished.finally.bind(input.finished),
  };
  return handle;
}

export function createRejectedTweenHandle(id: string, message: string): PlayableTweenHandle {
  return createTweenHandle({
    id,
    stop: () => {},
    finished: createObservedRejectedFinished(message),
  });
}

export interface NumericTweenInput {
  readonly id: string;
  readonly target: Record<string, number>;
  readonly props: PlayableTweenProps;
  readonly options: PlayableTweenOptions;
  readonly requestFrame: (callback: FrameRequestCallback) => number;
  readonly cancelFrame: (handle: number) => void;
  readonly now: () => number;
  readonly onUpdate?: (() => void) | undefined;
  readonly probeTargetLayerId?: string | null | undefined;
  readonly onProbeUpdate?: ((snapshot: RuntimeProbeTweenSnapshot) => void) | undefined;
}

export function createNumericTween(input: NumericTweenInput): PlayableTweenHandle {
  const tweenProps = input.props as Record<string, number>;
  const unsupported = validateTweenProps(tweenProps);
  if (unsupported.length > 0) {
    return createRejectedTweenHandle(input.id, `unsupported tween props: ${unsupported.join(',')}`);
  }
  if (!validateTweenEasing(input.options.easing)) {
    return createRejectedTweenHandle(input.id, `unsupported tween easing: ${String(input.options.easing)}`);
  }

  const entries = Object.entries(tweenProps);
  // Cast to unknown-valued map so we can inspect PixiJS ObservablePoint objects on the 'scale' property
  // without TypeScript complaining about impossible conditions on Record<string, number>.
  const targetUnknown = input.target as Record<string, unknown>;
  const startValues = Object.fromEntries(
    entries.map(([key]) => {
      const raw = targetUnknown[key];
      // ObservablePoint (used by PixiJS for .scale) stores the numeric value in .x; reading the object
      // directly would give NaN. Fall back to .x if the raw value is an object with a numeric .x.
      const numeric =
        key === 'scale' && raw !== null && typeof raw === 'object'
          ? ((raw as Record<string, unknown>).x as number | undefined) ?? 0
          : (raw as number | undefined) ?? 0;
      return [key, numeric];
    }),
  ) as Record<string, number>;
  const startAt = input.now();
  const durationMs = resolveTweenDurationMs(input.options);
  const delayMs = resolveTweenDelayMs(input.options);
  let frameId: number | null = null;
  let finished = false;
  let stopped = false;
  let resolveFinished!: () => void;

  const finishedPromise = new Promise<void>((resolve) => {
    resolveFinished = resolve;
  });

  const complete = () => {
    if (finished) return;
    finished = true;
    input.onProbeUpdate?.(probeSnapshot('finished', 1));
    resolveFinished();
  };

  const probeSnapshot = (
    status: RuntimeProbeTweenSnapshot['status'],
    progress: number,
  ): RuntimeProbeTweenSnapshot => ({
    id: input.id,
    targetLayerId: input.probeTargetLayerId ?? null,
    props: Object.fromEntries(entries.map(([key, endValue]) => {
      const startValue = startValues[key] ?? 0;
      const rawCurrent = targetUnknown[key];
      const current =
        key === 'scale' && rawCurrent !== null && typeof rawCurrent === 'object'
          ? ((rawCurrent as Record<string, unknown>).x as number | undefined) ?? startValue
          : (rawCurrent as number | undefined) ?? startValue;
      return [key, {
        from: startValue,
        to: endValue,
        current,
      }];
    })),
    easing: input.options.easing ?? 'linear',
    startedAtMs: startAt,
    durationMs,
    progress,
    status,
  });

  const tick = () => {
    if (stopped) return;

    const elapsed = input.now() - startAt - delayMs;
    const progress = elapsed <= 0 ? 0 : Math.min(1, elapsed / durationMs);
    const easedProgress = easeTweenProgress(progress, input.options.easing);

    for (const [key, endValue] of entries) {
      const startValue = startValues[key] ?? 0;
      const v = startValue + (endValue - startValue) * easedProgress;
      // ObservablePoint (PixiJS .scale): use .set(v) so both .x and .y update atomically.
      const raw = targetUnknown[key];
      if (key === 'scale' && raw !== null && typeof raw === 'object' && typeof (raw as Record<string, unknown>).set === 'function') {
        (raw as { set(v: number): void }).set(v);
      } else {
        input.target[key] = v;
      }
    }
    input.onUpdate?.();

    if (progress >= 1) {
      complete();
      return;
    }

    input.onProbeUpdate?.(probeSnapshot('running', progress));

    frameId = input.requestFrame(tick);
  };

  // Emit an immediate probe snapshot at progress=0 so the tween is visible in activeTweens from the moment
  // it is created — before the first requestFrame tick fires. Consumers checking activeTweens synchronously
  // after tweenLayer() is called (e.g. right after a flight sprite mounts) will see the tween immediately.
  input.onProbeUpdate?.(probeSnapshot('running', 0));

  frameId = input.requestFrame(tick);

  return createTweenHandle({
    id: input.id,
    finished: finishedPromise,
    stop: () => {
      if (finished || stopped) return;
      stopped = true;
      if (frameId !== null) input.cancelFrame(frameId);
      input.onProbeUpdate?.(probeSnapshot('stopped', 1));
      complete();
    },
  });
}
